// Apporteurs de partenaires (D-096) : accès aux données. Les règles sont dans la base : un compte
// mineur n'apporte pas, l'apporteur ne voit que ses pistes, seul le bureau change l'état, l'entreprise
// (sans compte) ne lit que l'offre du club.
import { supabase } from './supabase';
import type { LeadSource, LeadStatus, OfferNumbers } from '../domain/apporteurs';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};

/** Appel d'une fonction de la base ; l'erreur garde son code (23505 : doublon, 54000 : limite du jour) et son indice. */
async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code, hint: error.hint });
  return data as T;
}
async function must<T>(q: PromiseLike<{ data: T; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data;
}

// --- L'apporteur -------------------------------------------------------------------------
export interface RefWho { first: string | null; short: string; long: string; intro: string }
export interface RefHome {
  can: boolean; points?: boolean; packs?: number; packs_since?: string | null; gift?: string | null; members?: number;
  season?: { leads: number; signed: number }; mine?: { leads: number; signed: number }; me?: RefWho;
}
export const refHome = (clubId: string) => call<RefHome>('partner_ref_home', { p_club: clubId });
export const refLink = (clubId: string) => call<string>('partner_ref_link', { p_club: clubId });
export type RefCheck = null | 'partenaire' | 'piste' | 'mienne';
export const refCheck = (clubId: string, company: string) => call<RefCheck>('partner_ref_check', { p_club: clubId, p_company: company });
export interface ContactForm { company: string; contact_name: string | null; email: string | null; phone: string | null; relation: string | null }
export const refGiveContact = (clubId: string, f: ContactForm) => call<{ id: string; points: number }>('partner_ref_give_contact', {
  p_club: clubId, p_company: f.company, p_contact_name: f.contact_name, p_email: f.email, p_phone: f.phone, p_relation: f.relation, p_consent: true,
});

export interface MyLead {
  company: string; contact_name: string | null; source: LeadSource; status: LeadStatus | null; created_at: string; mine: boolean;
  dup: 'partenaire' | 'piste' | null; pack: string | null; points: number; gift: string | null;
}
export const myLeads = async (clubId: string) => (await call<MyLead[] | null>('my_partner_referrals', { p_club: clubId })) ?? [];
export interface Ambassador { name: string; signed: number; me: boolean }
export const ambassadors = async (clubId: string) => (await call<Ambassador[] | null>('partner_ambassadors', { p_club: clubId })) ?? [];

// --- Le bureau ---------------------------------------------------------------------------
export interface AdminLead {
  id: string; company: string; contact_name: string | null; email: string | null; phone: string | null; message: string | null;
  status: LeadStatus; created_at: string; status_at: string; source: LeadSource; relation: string | null; dup: 'partenaire' | 'piste' | null;
  pack: string | null; referrer: string | null; via: string | null; late: boolean;
}
export const leadsAdmin = async (clubId: string) => (await call<AdminLead[] | null>('partner_leads_admin', { p_club: clubId })) ?? [];
export const setLeadState = (id: string, status: LeadStatus) => must(db().from('partner_leads').update({ status }).eq('id', id));

export interface Pack { id: string; club_id: string; name: string; price_cents: number | null; content: string | null; position: number; created_at: string }
export const clubPacks = async (clubId: string) =>
  ((await must(db().from('partner_packs').select('id, club_id, name, price_cents, content, position, created_at').eq('club_id', clubId)
    .order('position').order('created_at'))) ?? []) as Pack[];
export type PackDraft = { id?: string; club_id: string; name: string; price_cents: number | null; content: string | null; position: number };
export const savePack = ({ id, ...rest }: PackDraft) =>
  (id ? must(db().from('partner_packs').update(rest).eq('id', id)) : must(db().from('partner_packs').insert(rest)));
export const deletePack = (id: string) => must(db().from('partner_packs').delete().eq('id', id));

export interface Program { club_id: string; gift: string | null; app_option: boolean; app_price_cents: number | null }
export const clubProgram = async (clubId: string) =>
  (await must(db().from('partner_program').select('club_id, gift, app_option, app_price_cents').eq('club_id', clubId).maybeSingle())) as Program | null;
export const saveProgram = (p: Program) => must(db().from('partner_program').upsert({ ...p, updated_at: new Date().toISOString() }));

// --- L'entreprise (sans compte) ------------------------------------------------------------
export interface Offer {
  club: { name: string; short_name: string | null; logo: string | null; color: string | null; color2: string | null; public: string | null };
  season: string | null;
  recommender: { first: string; role: string } | null;
  numbers: OfferNumbers;
  packs: { id: string; name: string; price_cents: number | null; content: string | null }[];
  app: { price_cents: number | null } | null;
  official: { name: string; logo: string | null } | null;
  partners: { id: string; name: string; logo: string | null; website: string | null }[];
}
export const partnerOffer = (slug: string | null, token: string | null) => call<Offer | null>('partner_offer', { p_slug: slug, p_token: token });
export interface OfferForm { company: string; contact_name: string | null; email: string | null; phone: string | null; message: string | null; pack: string | null }
export const offerRequest = (slug: string | null, token: string | null, f: OfferForm) => call<boolean>('partner_offer_request', {
  p_slug: slug, p_token: token, p_company: f.company, p_contact_name: f.contact_name, p_email: f.email, p_phone: f.phone,
  p_message: f.message, p_pack: f.pack, p_consent: true,
});
